import React, { useState } from "react";
import Cookie from "js-cookie";
import api from "../services/api.service";
import { useNavigate } from "react-router-dom";
import { useCombinedStore } from "../store";
import * as AlfredoStyles from "../styles/components/LoginModal";

const LoginModal: React.FC = ({ setIsLoading }: any) => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [isVisible] = useState(true);
  const [formData, setFormData] = useState({ userEmail: "", userPassword: "" });

  const { setAuthUserZustand, setOrganizationZustand, setMessageZustand } =
    useCombinedStore((state) => {
      return {
        setAuthUserZustand: state.setAuthUserZustand,
        setOrganizationZustand: state.setOrganizationZustand,
        setMessageZustand: state.setMessageZustand,
      };
    });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({ ...prevState, [name]: value }));
  };

  /**
   * O tratamento da resposta de login, extraído do `handleSubmit` SEM mudar uma
   * linha do que ele fazia. Existe porque agora há dois caminhos que produzem a
   * MESMA resposta (`/login` e `/demo/login`, que devolve o mesmo envelope de
   * propósito), e duplicar isto seria criar dois lugares para esquecer de mexer.
   *
   * Devolve `true` quando entrou, para quem chamou decidir o que fazer no erro.
   */
  const entrarComResposta = (response: any): boolean => {
    if (response.status !== 200) {
      setMessageZustand(response.data.message, "error");
      return false;
    }

    setAuthUserZustand({
      authUser: {
        Token: response.data.authUser.Token,
        RefreshToken: response.data.authUser.RefreshToken,
      },
    });

    setOrganizationZustand({
      InfoToken: response.data.authUser.InfoToken,
    });

    Cookie.remove("refreshToken");
    Cookie.set("refreshToken", response.data.authUser.RefreshToken, {
      expires: 30,
      secure: true,
    });

    navigate("/");
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    await new Promise((resolve) => setTimeout(resolve, 3000));
    try {
      const response = await api.post("/login", formData);
      entrarComResposta(response);
    } catch (error: any) {
      setMessageZustand(`Erro ao fazer login: ${error.message}`, "error");
    } finally {
      setIsLoading(false); // Garantir que o loading sempre pare
    }
  };

  /**
   * ENTRAR NA DEMONSTRAÇÃO — sem e-mail, sem senha, sem nada digitado.
   *
   * A credencial do usuário fictício vive no AMBIENTE DO SERVIDOR da demo; o
   * `POST /demo/login` autentica lá dentro e devolve os mesmos três tokens do
   * `/login`. É por isso que a senha nunca precisa ser dita a ninguém numa
   * apresentação comercial — e por isso ela não está aqui.
   *
   * Sem os 3 segundos de espera artificial do `handleSubmit`: numa reunião, três
   * segundos parados na tela de login é o pior lugar para gastá-los.
   */
  const handleDemo = async () => {
    setIsLoading(true);
    try {
      const response = await api.post("/demo/login");
      entrarComResposta(response);
    } catch (error: any) {
      setMessageZustand(
        `Não foi possível entrar na demonstração: ${
          error?.response?.data?.message ?? error.message
        }`,
        "error"
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-modal">
      {isVisible && (
        <form onSubmit={handleSubmit}>
          <div className="input-group">
            <AlfredoStyles.IconLabel className="icon-label">
              contact_mail
            </AlfredoStyles.IconLabel>
            <AlfredoStyles.TextInput>
              <input
                type="email"
                name="userEmail"
                value={formData.userEmail}
                onChange={handleChange}
                required
              />
              <label>Digite seu email.</label>
            </AlfredoStyles.TextInput>
          </div>
          <div className="input-group">
            <AlfredoStyles.IconLabel className="icon-label">
              password
            </AlfredoStyles.IconLabel>
            <AlfredoStyles.PasswordWrapper>
              <AlfredoStyles.TextInput>
                <input
                  type={showPassword ? "text" : "password"}
                  name="userPassword"
                  value={formData.userPassword}
                  onChange={handleChange}
                  required
                />
                <label>Digite sua senha.</label>
              </AlfredoStyles.TextInput>
              <AlfredoStyles.TogglePasswordButton
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
              >
                <AlfredoStyles.IconInput className="icon-input">
                  {showPassword ? "visibility" : "visibility_off"}
                </AlfredoStyles.IconInput>
              </AlfredoStyles.TogglePasswordButton>
            </AlfredoStyles.PasswordWrapper>
            <AlfredoStyles.ForgotPasswordLink href="/forgot-password">
              Esqueceu a senha?
            </AlfredoStyles.ForgotPasswordLink>
          </div>
          <AlfredoStyles.ButtonWrapper>
            <AlfredoStyles.Button>Entrar</AlfredoStyles.Button>
          </AlfredoStyles.ButtonWrapper>

          {/* O botão da apresentação comercial, e a variável tem que estar
              DECLARADA nos dois serviços — inclusive no de produção, com "nao".

              O CRA substitui `process.env` inteiro por um objeto literal com as
              REACT_APP_* que existiam no build. Com a variável AUSENTE, o que
              sobra é `"sim" === {…}.REACT_APP_DEMO` — uma leitura de
              propriedade que o minificador não dobra, então o bloco continua
              no bundle publicado (inerte, mas legível: medido em 23/08/2026 no
              main.js de produção, com o `await api.post("/demo/login")` à
              vista de quem abrir o arquivo).

              Com `REACT_APP_DEMO=nao` declarada, o literal vira `"sim" === "nao"`,
              o Terser dobra para `false` e o bloco SAI do bundle: 0 ocorrências
              de "Ver a demonstração" e de "/demo/login". É por isso que o
              serviço `alfredo-central` (produção) declara a variável com "nao"
              em vez de simplesmente não a ter. O mesmo vale para a faixa em
              `MainLayout.tsx`. */}
          {process.env.REACT_APP_DEMO === "sim" && (
            <>
              <AlfredoStyles.DemoButton type="button" onClick={handleDemo}>
                Ver a demonstração
              </AlfredoStyles.DemoButton>
              <AlfredoStyles.DemoHint>
                Entra direto, sem senha. Todos os dados são fictícios.
              </AlfredoStyles.DemoHint>
            </>
          )}
        </form>
      )}
    </div>
  );
};

export default LoginModal;
